import React from "react";
import * as Sentry from '@sentry/react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import PlayerCompare from "./components/playerCompare";
import Compare from "./components/playerCompare/compare";
import StartSit from "./components/startSit";
import StartSitCompare from "./components/startSit/compare";
import PostContest from './components/postContest';
import PreContest from './components/preContest';
import Trending from "./components/trending";
import Feedback from "./components/common/Feedback";
import { LicenseInfo } from '@mui/x-license-pro';
import ContestGenerator from "./components/contestGenerator";
import LateSwap from "./components/lateSwap";
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import Projections from "./components/projections";
import LineupBuilder from "./components/LineupBuilder";
import Pick6 from "./components/pick6";
import PickemOptimizer from "./components/pickemOptimizer";
import StokasticProps from "./components/stokasticProps";
import { DEFAULT_PROPS_LEAGUE } from "./components/stokasticProps/data/leagueConfig";
import NbaRates from "./components/nbaRates";
import { DailyStorageCleanup } from "./utils/dailyStorageCleanup";
import { setPersistentUserId, encodeEmail } from "./utils/persistentUserId";
import { clearUserCookie, setUserCookie } from "./utils/userCookie";
import { getIsMobileApp } from "./utils/mobileApp";
import LandingPage from "./components/landing";
import Schedule from "./components/schedule";
import Rotations from "./components/rotations";
import PopupManager from "./components/common/PopupManager";
import BannerManager from "./components/common/BannerManager";
import MobileAppBottomNav from "./components/common/MobileAppBottomNav";
import AccountPage from "./components/account/AccountPage";
import RequireAuth from "./components/common/RequireAuth";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY ?? '');


function App() {
  const { isLoading, isAuthenticated, user } = useAuth0();

  // Run daily localStorage cleanup on first visit each day
  React.useEffect(() => {
    DailyStorageCleanup.performDailyCleanup();
  }, []);

  // Sync Auth0 user to cookie for Entry Manager and other features
  React.useEffect(() => {
    if (!isLoading && isAuthenticated && user?.email) {
      const email = user.email.toLowerCase().trim();
      const encodedId = encodeEmail(email);

      // Set persistent user ID in localStorage
      setPersistentUserId(email);

      // Check if cookie needs to be set/updated
      const existingCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('user='));

      let needsUpdate = true;
      if (existingCookie) {
        try {
          const cookieValue = existingCookie.split('=').slice(1).join('=');
          const decoded = decodeURIComponent(cookieValue);
          const parsed = JSON.parse(decoded);
          // Only update if email is different
          if (parsed?.email === email) {
            needsUpdate = false;
          }
        } catch (e) {
          // Invalid cookie, will be replaced
        }
      }

      if (needsUpdate) {
        setUserCookie({
          id: encodedId,
          email,
          username: email,
        });
      }
    }
  }, [isLoading, isAuthenticated, user]);

  React.useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    clearUserCookie();
  }, [isLoading, isAuthenticated]);

  // Restore last visited tab for mobile app users
  React.useEffect(() => {
    const isMobileApp = getIsMobileApp();
    const params = new URLSearchParams(window.location.search);
    if (isMobileApp && params.get('defaultUrl') === 'true') {
      const lastTab = localStorage.getItem('mobileLastTab') as 'props' | 'datahub' | 'lineupsim' | null;
      const league = window.location.pathname.split('/').filter(Boolean)[1] || DEFAULT_PROPS_LEAGUE;
      if (lastTab === 'datahub') {
        window.location.replace(`/datahub/${league}?ismobileapp=true`);
      } else if (lastTab === 'lineupsim') {
        window.location.replace(`/singlelineupsim/${league}?ismobileapp=true`);
      }
    }
  }, []);

  // Check for post-logout redirect after Auth0 finishes loading
  React.useEffect(() => {
    if (!isLoading) {
      const savedPath = localStorage.getItem('stokastic_post_logout_redirect');
      if (savedPath) {
        localStorage.removeItem('stokastic_post_logout_redirect');
        // Only redirect if we're on a base path (no league in URL)
        const currentPath = window.location.pathname;
        const pathSegments = currentPath.split('/').filter(Boolean);
        // If we only have the app name (e.g., /precontestsimulator/) redirect to saved path
        if (pathSegments.length <= 1 && savedPath !== currentPath) {
          window.location.replace(savedPath);
        }
      }
    }
  }, [isLoading]);

  // Wait for Auth0 to finish loading before rendering routes
  // This prevents the router from redirecting before Auth0 processes the callback
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#000A16"
      >
        <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
      </Box>
    );
  }
  
  const SentryRoute = Sentry.withSentryRouting(Route);

  window.addEventListener('load', () => {
    const screenWidth = window.innerWidth;

    // Don't apply zoom on mobile devices (< 768px) - let them use native scaling
    if (screenWidth < 768) {
      document.body.style.setProperty('zoom', '1');
    } else if (screenWidth < 1400) {
      document.body.style.setProperty('zoom', '0.8'); // Zoom level for smaller desktop/tablet screens
    } else {
      document.body.style.setProperty('zoom', '1'); // Default zoom level for larger screens
    }
  });

  return (
    <Router>
      <QueryParamProvider adapter={ReactRouter5Adapter}>
        <BannerManager />
        <PopupManager />
        <Switch>
          {/* Public */}
          <SentryRoute exact path="/" component={LandingPage} />
          {/* Props is public so non-subscribers (signed out or DFS-only) see the teaser +
              upsell instead of being bounced to Auth0. The stokastic-site proxy strips the
              board for anyone without a Props subscription; the tool only presents it. */}
          <SentryRoute
            path="/props/:league"
            render={(props) => <StokasticProps key={props.match.params.league} {...props} />}
          />

          {/* URL normalizers — no auth gate, no component to render */}
          <Redirect exact from="/playercompare" to="/playercompare/NBA" />
          <Redirect exact from="/playercompare/" to="/playercompare/NBA" />
          <Redirect exact from="/startsit" to="/startsit/NFL" />
          <Redirect exact from="/startsit/" to="/startsit/NFL" />
          <Redirect exact from="/postcontestsimulator" to="/postcontestsimulator/NBA" />
          <Redirect exact from="/postcontestsimulator/" to="/postcontestsimulator/NBA" />
          <Redirect exact from="/precontestsimulator" to="/precontestsimulator/NBA" />
          <Redirect exact from="/precontestsimulator/" to="/precontestsimulator/NBA" />
          <Redirect exact from="/contestGenerator" to="/contestGenerator/NBA" />
          <Redirect exact from="/contestGenerator/" to="/contestGenerator/NBA" />
          <Redirect exact from="/lateswap" to="/lateswap/NBA" />
          <Redirect exact from="/lateswap/" to="/lateswap/NBA" />
          <Redirect exact from="/datahub" to="/datahub/NBA" />
          <Redirect exact from="/datahub/" to="/datahub/NBA" />
          <Redirect exact from="/singlelineupsim" to="/singlelineupsim/NBA" />
          <Redirect exact from="/singlelineupsim/" to="/singlelineupsim/NBA" />
          <Redirect exact from="/rotations" to="/rotations/NBA" />
          <Redirect exact from="/rotations/" to="/rotations/NBA" />
          <Redirect from="/lineupbuilder/:league" to="/singlelineupsim/:league" />
          <Redirect exact from="/props" to={`/props/${DEFAULT_PROPS_LEAGUE}`} />
          <Redirect exact from="/props/" to={`/props/${DEFAULT_PROPS_LEAGUE}`} />
          <Redirect exact from="/nbarates" to="/nbarates/NBA" />
          <Redirect exact from="/nbarates/" to="/nbarates/NBA" />

          {/* All routes below require authentication. RequireAuth mounts once
              and gates the inner Switch. */}
          <Route>
            <RequireAuth>
              <Switch>
                <SentryRoute exact path="/account" component={AccountPage} />
                <SentryRoute exact path="/playercompare/:league" component={PlayerCompare} />
                <SentryRoute path="/playercompare/:league/trends" component={Trending} />
                <SentryRoute path="/playercompare/:league/compare" component={Compare} />
                <SentryRoute exact path="/startsit/:league" component={StartSit} />
                <SentryRoute path="/startsit/:league/compare" component={StartSitCompare} />
                <SentryRoute path="/postcontestsimulator/:league/feedback" component={Feedback} />
                <SentryRoute path="/precontestsimulator/:league/feedback" component={Feedback} />
                <SentryRoute
                  path="/postcontestsimulator/:league"
                  render={(props) => <PostContest key={props.match.params.league} {...props} />}
                />
                <SentryRoute
                  path="/precontestsimulator/:league"
                  render={(props) => <PreContest key={props.match.params.league} {...props} />}
                />
                <SentryRoute
                  path="/contestGenerator/:league"
                  render={(props) => <ContestGenerator key={props.match.params.league} {...props} />}
                />
                <SentryRoute
                  path="/lateswap/:league"
                  render={(props) => <LateSwap key={props.match.params.league} {...props} />}
                />
                <SentryRoute
                  path="/datahub/:league"
                  render={(props) => <Projections key={props.match.params.league} {...props} />}
                />
                <SentryRoute
                  path="/singlelineupsim/:league"
                  render={(props) => <LineupBuilder key={props.match.params.league} {...props} />}
                />
                <SentryRoute path="/schedule" component={Schedule} />
                <SentryRoute path="/fantasy-optimizers/pick6" component={Pick6} />
                <SentryRoute path="/fantasy-optimizers/prizepicks" component={PickemOptimizer} />
                <SentryRoute path="/fantasy-optimizers/underdog" component={PickemOptimizer} />
                <SentryRoute path="/nbarates/:league" component={NbaRates} />
                <SentryRoute
                  path="/rotations/:league"
                  render={(props) => <Rotations key={props.match.params.league} {...props} />}
                />
              </Switch>
            </RequireAuth>
          </Route>
        </Switch>
        <MobileAppBottomNav />
      </QueryParamProvider>
    </Router>
  );
}

export default App;
